// Section 3: Graphs — improved and corrected GCSE topic data
// Image URLs reference the original workbook page scans

const PAGE_IMAGES = {
  p56_57: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/qvdk9pnv_Page56-57.png",
  p58_59: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/oqan9u90_Page58-59.png",
  p60_61: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/wojg6qp6_Page60-61.png",
  p62_63: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/tpn7d2yt_Page62-63.png",
  p64_65: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/u50jj36b_Page64-65.png",
  p66_67: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/ic9uedvv_Page66-67.png",
  p68_69: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/ubpxrcsy_Page68-69.png",
  p70_71: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/2ure0228_Page70-71.png",
  p72_73: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/7euuxkh6_Page72-73.png",
  p74_75: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/7x9qh7mh_Page74-75.png",
};

export const graphsTopics = [
  {
    id: "3.1",
    title: "Coordinates & Midpoints",
    slug: "coordinates-midpoints",
    pageRef: "Pages 57-58",
    difficulty: "foundation",
    learn: {
      explanation:
        "Coordinates tell us the position of a point on a grid using (x, y). The x-coordinate tells you how far across to move, and the y-coordinate tells you how far up or down to move. A midpoint is the exact centre of a line segment.",
      keyPoints: [
        "Coordinates are always written as (x, y)",
        "x-axis = horizontal, y-axis = vertical",
        "The origin is (0, 0)",
        "Four quadrants: (+,+), (−,+), (−,−), (+,−)",
        "Midpoint = (average of the x-coordinates, average of the y-coordinates)",
        "A midpoint can contain decimals or fractions"
      ],
      workedExamples: [
        {
          problem: "Find the midpoint of R(1, 1) and S(5, 6)",
          image: PAGE_IMAGES.p56_57,
          steps: [
            { step: 1, instruction: "Average the x-coordinates", working: "(1 + 5) ÷ 2 = 3" },
            { step: 2, instruction: "Average the y-coordinates", working: "(1 + 6) ÷ 2 = 3.5" },
            { step: 3, instruction: "Write as a coordinate", working: "(3, 3.5)" }
          ],
          answer: "(3, 3.5)"
        },
        {
          problem: "Find the midpoint of A(−2, 7) and B(6, −1)",
          steps: [
            { step: 1, instruction: "Average the x-coordinates", working: "(−2 + 6) ÷ 2 = 2" },
            { step: 2, instruction: "Average the y-coordinates", working: "(7 + −1) ÷ 2 = 3" }
          ],
          answer: "(2, 3)"
        }
      ],
      examTips: [
        "Never reverse the order: (x, y), not (y, x)",
        "Use brackets and a comma when giving coordinate answers",
        "If the answer is halfway between two integers, decimals such as 3.5 are acceptable"
      ],
      commonMistakes: [
        "Writing y first instead of x",
        "Forgetting negative signs",
        "Adding coordinates but forgetting to divide by 2"
      ],
      whyItWorks:
        "The midpoint is the point exactly halfway between two endpoints. Averaging the x-values puts you halfway across, and averaging the y-values puts you halfway up or down."
    },
    practice: {
      level1: [
        {
          id: "3.1.1",
          type: "text",
          question: "What are the coordinates of the origin?",
          answer: "(0, 0)",
          acceptedAnswers: ["(0,0)", "0,0"],
          hint1: "The origin is where the two axes cross.",
          hint2: "Both x and y are zero.",
          hint3: "(0, 0)"
        },
        {
          id: "3.1.2",
          type: "text",
          question: "Find the midpoint of (2, 4) and (6, 8).",
          answer: "(4, 6)",
          acceptedAnswers: ["(4,6)", "4,6"],
          hint1: "Average the x-coordinates: (2 + 6) ÷ 2.",
          hint2: "Average the y-coordinates: (4 + 8) ÷ 2.",
          hint3: "(4, 6)"
        }
      ],
      level2: [
        {
          id: "3.1.3",
          type: "text",
          question: "Find the midpoint of (3, 8) and (7, 10).",
          answer: "(5, 9)",
          acceptedAnswers: ["(5,9)", "5,9"],
          hint1: "x: (3 + 7) ÷ 2 = 5.",
          hint2: "y: (8 + 10) ÷ 2 = 9.",
          hint3: "(5, 9)"
        },
        {
          id: "3.1.4",
          type: "text",
          question: "Find the midpoint of (−1, 3) and (5, −1).",
          answer: "(2, 1)",
          acceptedAnswers: ["(2,1)", "2,1"],
          hint1: "x: (−1 + 5) ÷ 2 = 2.",
          hint2: "y: (3 + −1) ÷ 2 = 1.",
          hint3: "(2, 1)"
        }
      ],
      level3: [
        {
          id: "3.1.5",
          type: "text",
          question: "M is the midpoint of A(−4, 2) and B(8, 10). Find M.",
          answer: "(2, 6)",
          acceptedAnswers: ["(2,6)", "2,6"],
          hint1: "Average −4 and 8.",
          hint2: "Average 2 and 10.",
          hint3: "M = (2, 6)."
        }
      ]
    }
  },

  {
    id: "3.2",
    title: "Straight Line Graphs",
    slug: "straight-line-graphs",
    pageRef: "Pages 58-61",
    difficulty: "foundation",
    learn: {
      explanation:
        "Straight line graphs are produced by linear equations. The most common form is y = mx + c, where m controls the steepness and c shows where the line crosses the y-axis.",
      keyPoints: [
        "Horizontal lines have equations like y = 3",
        "Vertical lines have equations like x = −2",
        "y = x goes diagonally up through the origin",
        "y = −x goes diagonally down through the origin",
        "To plot a line, make a table of values, plot the points, then join with a ruler",
        "A straight line needs at least two correct points, but three points help check accuracy"
      ],
      workedExamples: [
        {
          problem: "Plot y = 2x + 1 for x = 0, 1 and 2.",
          image: PAGE_IMAGES.p58_59,
          steps: [
            { step: 1, instruction: "Substitute x = 0", working: "y = 2(0) + 1 = 1, so point is (0, 1)" },
            { step: 2, instruction: "Substitute x = 1", working: "y = 2(1) + 1 = 3, so point is (1, 3)" },
            { step: 3, instruction: "Substitute x = 2", working: "y = 2(2) + 1 = 5, so point is (2, 5)" },
            { step: 4, instruction: "Plot and join", working: "Draw a straight line through the points" }
          ],
          answer: "A straight line through (0, 1), (1, 3) and (2, 5)"
        }
      ],
      examTips: [
        "Use a ruler for straight lines",
        "Label axes clearly",
        "Check your line crosses the y-axis where expected",
        "If one plotted point does not line up, recheck the substitution"
      ],
      commonMistakes: [
        "Plotting x and y the wrong way round",
        "Drawing a curved line for a linear equation",
        "Confusing x = 3 with y = 3"
      ],
      whyItWorks:
        "A linear equation has a constant rate of change. That means every equal increase in x gives the same increase or decrease in y, creating a straight line."
    },
    practice: {
      level1: [
        {
          id: "3.2.1",
          type: "text",
          question: "What is the equation of a horizontal line through y = 5?",
          answer: "y = 5",
          acceptedAnswers: ["y=5"],
          hint1: "Horizontal lines have y = something.",
          hint2: "The y-value is always 5.",
          hint3: "y = 5"
        },
        {
          id: "3.2.2",
          type: "text",
          question: "What is the equation of a vertical line through x = −3?",
          answer: "x = -3",
          acceptedAnswers: ["x=-3", "x = −3", "x=−3"],
          hint1: "Vertical lines have x = something.",
          hint2: "The x-value is always −3.",
          hint3: "x = −3"
        }
      ],
      level2: [
        {
          id: "3.2.3",
          type: "number",
          question: "Find the y-coordinate when x = 3 on the line y = −3x + 5.",
          answer: "-4",
          hint1: "Substitute x = 3.",
          hint2: "y = −3(3) + 5.",
          hint3: "y = −9 + 5 = −4."
        },
        {
          id: "3.2.4",
          type: "number",
          question: "Find the y-coordinate when x = 0 on the line y = 4x + 5.",
          answer: "5",
          hint1: "Substitute x = 0.",
          hint2: "y = 4(0) + 5.",
          hint3: "y = 5."
        }
      ],
      level3: [
        {
          id: "3.2.5",
          type: "text",
          question: "Complete the coordinate for y = 3x − 2 when x = 4.",
          answer: "(4, 10)",
          acceptedAnswers: ["(4,10)", "4,10"],
          hint1: "Substitute x = 4.",
          hint2: "y = 3(4) − 2 = 10.",
          hint3: "The coordinate is (4, 10)."
        }
      ]
    }
  },

  {
    id: "3.3",
    title: "Gradient & y = mx + c",
    slug: "gradient-ymxc",
    pageRef: "Pages 62-67",
    difficulty: "intermediate",
    learn: {
      explanation:
        "The gradient tells you how steep a line is. In y = mx + c, m is the gradient and c is the y-intercept, where the graph crosses the y-axis.",
      keyPoints: [
        "Gradient = change in y ÷ change in x",
        "Gradient is also called rise over run",
        "Positive gradient means the line slopes upwards from left to right",
        "Negative gradient means the line slopes downwards from left to right",
        "In y = mx + c, m is the gradient and c is the y-intercept",
        "Parallel lines have the same gradient",
        "To find a line equation, use y = mx + c and substitute a known point"
      ],
      workedExamples: [
        {
          problem: "Find the gradient between (1, 10) and (7, 40).",
          image: PAGE_IMAGES.p62_63,
          steps: [
            { step: 1, instruction: "Find the change in y", working: "40 − 10 = 30" },
            { step: 2, instruction: "Find the change in x", working: "7 − 1 = 6" },
            { step: 3, instruction: "Divide", working: "gradient = 30 ÷ 6 = 5" }
          ],
          answer: "5"
        },
        {
          problem: "Find m and c for the line 2x + y = 4.",
          image: PAGE_IMAGES.p64_65,
          steps: [
            { step: 1, instruction: "Rearrange into y = mx + c", working: "y = −2x + 4" },
            { step: 2, instruction: "Read off the gradient and intercept", working: "m = −2, c = 4" }
          ],
          answer: "m = −2, c = 4"
        },
        {
          problem: "Find the equation of a line with gradient −2 passing through (5, 0).",
          steps: [
            { step: 1, instruction: "Start with y = mx + c", working: "y = −2x + c" },
            { step: 2, instruction: "Substitute the point", working: "0 = −2(5) + c" },
            { step: 3, instruction: "Solve for c", working: "0 = −10 + c, so c = 10" }
          ],
          answer: "y = −2x + 10"
        }
      ],
      examTips: [
        "Always rearrange into y = mx + c before reading m and c",
        "A negative gradient must include the minus sign",
        "For parallel lines, compare gradients only, not y-intercepts",
        "Use two clear points on the line to calculate gradient from a graph"
      ],
      commonMistakes: [
        "Using change in x ÷ change in y instead of change in y ÷ change in x",
        "Forgetting that y = −x + 6 has gradient −1",
        "Thinking same y-intercept means parallel"
      ],
      whyItWorks:
        "Gradient is a rate of change. It tells you how much y changes when x increases by 1."
    },
    practice: {
      warmUp: [
        {
          id: "3.3.w1",
          type: "number",
          question: "What is the gradient of y = 3x + 7?",
          answer: "3",
          hint1: "Compare with y = mx + c.",
          hint2: "m is the gradient.",
          hint3: "m = 3."
        },
        {
          id: "3.3.w2",
          type: "number",
          question: "What is the y-intercept of y = 3x + 7?",
          answer: "7",
          hint1: "Compare with y = mx + c.",
          hint2: "c is the y-intercept.",
          hint3: "c = 7."
        }
      ],
      level1: [
        {
          id: "3.3.1",
          type: "number",
          question: "Find the gradient between (0, 2) and (3, 8).",
          answer: "2",
          hint1: "Change in y: 8 − 2 = 6.",
          hint2: "Change in x: 3 − 0 = 3.",
          hint3: "6 ÷ 3 = 2."
        },
        {
          id: "3.3.2",
          type: "text",
          question: "Find the gradient and y-intercept of y = −4x + 1.",
          answer: "m = -4, c = 1",
          acceptedAnswers: ["m=-4,c=1", "gradient -4 intercept 1", "gradient = -4, y-intercept = 1"],
          hint1: "m is the coefficient of x.",
          hint2: "c is the constant.",
          hint3: "m = −4, c = 1."
        }
      ],
      level2: [
        {
          id: "3.3.3",
          type: "text",
          question: "Write 6 − x = y in the form y = mx + c.",
          answer: "y = -x + 6",
          acceptedAnswers: ["y=-x+6", "y = 6 - x", "y=6-x"],
          hint1: "Put y on the left.",
          hint2: "y = 6 − x.",
          hint3: "y = −x + 6."
        },
        {
          id: "3.3.4",
          type: "choice",
          question: "Are y = 3x + 2 and y − 3x = 3 parallel?",
          options: ["Yes", "No"],
          answer: "Yes",
          hint1: "Rearrange the second equation.",
          hint2: "y = 3x + 3.",
          hint3: "Both gradients are 3, so they are parallel."
        }
      ],
      level3: [
        {
          id: "3.3.5",
          type: "text",
          question: "Find the equation of a line through (5, 0) with gradient −2.",
          answer: "y = -2x + 10",
          acceptedAnswers: ["y=-2x+10", "y = −2x + 10"],
          hint1: "Start with y = −2x + c.",
          hint2: "Substitute (5, 0): 0 = −10 + c.",
          hint3: "c = 10, so y = −2x + 10."
        },
        {
          id: "3.3.6",
          type: "text",
          question: "Find the equation of the line through (0, −3) with gradient 4.",
          answer: "y = 4x - 3",
          acceptedAnswers: ["y=4x-3", "y = 4x − 3"],
          hint1: "The point (0, −3) gives the y-intercept.",
          hint2: "m = 4 and c = −3.",
          hint3: "y = 4x − 3."
        }
      ]
    },
    aiChallenge: {
      prompt:
        "A line passes through A(2, 7) and B(6, 19). Find its gradient and equation. Explain each step clearly.",
      successCriteria: [
        "Calculates change in y: 19 − 7 = 12",
        "Calculates change in x: 6 − 2 = 4",
        "Finds gradient m = 3",
        "Uses y = 3x + c",
        "Substitutes one point correctly",
        "Finds c = 1",
        "Gives final equation y = 3x + 1"
      ]
    }
  },

  {
    id: "3.4",
    title: "Travel & Real-Life Graphs",
    slug: "travel-graphs",
    pageRef: "Pages 68-71",
    difficulty: "foundation",
    learn: {
      explanation:
        "Real-life graphs show how one quantity changes compared with another. Distance-time graphs show journeys. Conversion graphs, billing graphs and speed graphs are interpreted in a similar way: read the axes carefully and understand what the gradient means.",
      keyPoints: [
        "On a distance-time graph, flat means stationary",
        "A steeper line means a faster speed",
        "Going up means moving away from the start",
        "Going down means returning towards the start",
        "Speed = distance ÷ time",
        "On a distance-time graph, speed is the gradient",
        "Always check the units on both axes"
      ],
      workedExamples: [
        {
          problem: "A car travels 50 km, stops for 30 minutes, then returns. What does the flat section represent?",
          image: PAGE_IMAGES.p68_69,
          steps: [
            { step: 1, instruction: "Look at the distance", working: "The distance stays at 50 km." },
            { step: 2, instruction: "Look at the time", working: "Time continues to pass for 30 minutes." },
            { step: 3, instruction: "Interpret", working: "Distance is not changing, so the car is stationary." }
          ],
          answer: "The car has stopped for 30 minutes."
        },
        {
          problem: "A cyclist travels 18 km in 1.5 hours. Find the speed.",
          steps: [
            { step: 1, instruction: "Use speed = distance ÷ time", working: "18 ÷ 1.5" },
            { step: 2, instruction: "Calculate", working: "18 ÷ 1.5 = 12" }
          ],
          answer: "12 km/h"
        }
      ],
      examTips: [
        "Read the scale carefully before taking values from a graph",
        "State units in real-life graph answers",
        "Flat on a distance-time graph means stopped, not moving at constant speed",
        "A straight sloping line means constant speed"
      ],
      commonMistakes: [
        "Saying a flat distance-time graph means constant speed",
        "Mixing minutes and hours in speed calculations",
        "Ignoring the scale on the axes"
      ],
      whyItWorks:
        "Graphs show relationships visually. The steepness of a distance-time graph tells us how much distance changes for each unit of time."
    },
    practice: {
      level1: [
        {
          id: "3.4.1",
          type: "choice",
          question: "On a distance-time graph, what does a flat horizontal line mean?",
          options: ["The object is speeding up", "The object has stopped", "The object is going backwards", "The object is at constant speed"],
          answer: "The object has stopped",
          hint1: "Flat means no change in distance.",
          hint2: "Time passes but distance does not change.",
          hint3: "The object has stopped."
        },
        {
          id: "3.4.2",
          type: "choice",
          question: "On a distance-time graph, what does a steeper line mean?",
          options: ["Slower speed", "Faster speed", "Stopped", "Going backwards"],
          answer: "Faster speed",
          hint1: "Steeper means more distance in the same time.",
          hint2: "More distance per unit time means faster.",
          hint3: "Faster speed."
        }
      ],
      level2: [
        {
          id: "3.4.3",
          type: "number",
          question: "A conversion graph shows 25 miles = 40 km. How many km is 35 miles?",
          answer: "56",
          hint1: "Find km per mile: 40 ÷ 25 = 1.6.",
          hint2: "35 × 1.6.",
          hint3: "56 km."
        },
        {
          id: "3.4.4",
          type: "number",
          question: "A runner travels 12 km in 2 hours. Find the speed in km/h.",
          answer: "6",
          hint1: "Speed = distance ÷ time.",
          hint2: "12 ÷ 2.",
          hint3: "6 km/h."
        }
      ],
      level3: [
        {
          id: "3.4.5",
          type: "number",
          question: "A car travels 90 km in 1 hour 30 minutes. Find the speed in km/h.",
          answer: "60",
          hint1: "Convert 1 hour 30 minutes to 1.5 hours.",
          hint2: "Speed = 90 ÷ 1.5.",
          hint3: "60 km/h."
        }
      ]
    }
  },

  {
    id: "3.5",
    title: "Simultaneous Equations from Graphs",
    slug: "simultaneous-equations",
    pageRef: "Pages 72-73",
    difficulty: "intermediate",
    learn: {
      explanation:
        "The solution to simultaneous equations is the point where the two graphs intersect. At that point, the same x and y values satisfy both equations.",
      keyPoints: [
        "Plot both lines on the same axes",
        "The intersection gives the solution",
        "Read both x and y values from the crossing point",
        "Check by substituting the point into both equations",
        "Parallel lines have no solution",
        "Identical lines have infinitely many solutions"
      ],
      workedExamples: [
        {
          problem: "Solve y = 3x + 1 and y = 5 − x graphically.",
          image: PAGE_IMAGES.p72_73,
          steps: [
            { step: 1, instruction: "Plot y = 3x + 1", working: "Useful points: (0, 1), (1, 4), (2, 7)" },
            { step: 2, instruction: "Plot y = 5 − x", working: "Useful points: (0, 5), (1, 4), (2, 3)" },
            { step: 3, instruction: "Find the intersection", working: "The lines cross at (1, 4)." },
            { step: 4, instruction: "Check", working: "3(1) + 1 = 4 and 5 − 1 = 4." }
          ],
          answer: "x = 1, y = 4"
        }
      ],
      examTips: [
        "The answer should usually be a coordinate or x and y values",
        "Graphical solutions may be approximate if read from a graph",
        "Use substitution to check exact answers when possible"
      ],
      commonMistakes: [
        "Only giving the x-value",
        "Reading the y-intercept instead of the intersection",
        "Solving one equation only"
      ],
      whyItWorks:
        "At the crossing point, the point lies on both graphs. That means both equations are true at the same time."
    },
    practice: {
      level1: [
        {
          id: "3.5.1",
          type: "choice",
          question: "How do you find the solution to simultaneous equations on a graph?",
          options: ["Where the lines cross", "Where they touch the x-axis", "The y-intercept", "The gradient"],
          answer: "Where the lines cross",
          hint1: "The solution must satisfy both equations.",
          hint2: "Both lines pass through the same point.",
          hint3: "Use the intersection point."
        }
      ],
      level2: [
        {
          id: "3.5.2",
          type: "text",
          question: "y = x + 4 and y = 4x − 2 cross at what point?",
          answer: "(2, 6)",
          acceptedAnswers: ["(2,6)", "2,6", "x=2,y=6", "x = 2, y = 6"],
          hint1: "Set the equations equal: x + 4 = 4x − 2.",
          hint2: "6 = 3x, so x = 2.",
          hint3: "y = 2 + 4 = 6, so the point is (2, 6)."
        }
      ],
      level3: [
        {
          id: "3.5.3",
          type: "text",
          question: "Solve y = 2x + 3 and y = −x + 9.",
          answer: "x = 2, y = 7",
          acceptedAnswers: ["(2,7)", "2,7", "x=2,y=7", "x = 2, y = 7"],
          hint1: "Set the equations equal.",
          hint2: "2x + 3 = −x + 9.",
          hint3: "3x = 6, so x = 2 and y = 7."
        }
      ]
    }
  },

  {
    id: "3.6",
    title: "Quadratic Graphs",
    slug: "quadratic-graphs",
    pageRef: "Pages 73-75",
    difficulty: "higher",
    learn: {
      explanation:
        "Quadratic graphs have equations involving x². Their graphs are parabolas: U-shaped if the coefficient of x² is positive and upside-down U-shaped if it is negative.",
      keyPoints: [
        "A quadratic has the form y = ax² + bx + c",
        "If a is positive, the graph opens upwards",
        "If a is negative, the graph opens downwards",
        "Quadratic graphs are symmetrical about a vertical line",
        "Use a table of values to plot them accurately",
        "Join points with a smooth curve, not straight line segments",
        "Roots are where the graph crosses or touches the x-axis"
      ],
      workedExamples: [
        {
          problem: "Plot y = x² − 1 for x = −3 to 3.",
          image: PAGE_IMAGES.p74_75,
          steps: [
            { step: 1, instruction: "Make a table of x-values", working: "x: −3, −2, −1, 0, 1, 2, 3" },
            { step: 2, instruction: "Calculate y-values", working: "y: 8, 3, 0, −1, 0, 3, 8" },
            { step: 3, instruction: "Plot the points", working: "(−3,8), (−2,3), (−1,0), (0,−1), (1,0), (2,3), (3,8)" },
            { step: 4, instruction: "Join smoothly", working: "Draw a smooth U-shaped curve." }
          ],
          answer: "A U-shaped parabola with minimum point at (0, −1)."
        }
      ],
      examTips: [
        "Use at least 5 points, preferably 7, for a reliable sketch",
        "Never use a ruler to join points on a quadratic graph",
        "Use symmetry to check your table values",
        "When solving from a graph, read the x-values where the required horizontal line crosses the curve"
      ],
      commonMistakes: [
        "Forgetting that negative numbers square to positive values",
        "Joining points with straight lines",
        "Reading the y-value instead of the x-value when solving"
      ],
      whyItWorks:
        "The x² term creates symmetry because positive and negative x-values can produce the same square."
    },
    practice: {
      level1: [
        {
          id: "3.6.1",
          type: "choice",
          question: "What shape is a quadratic graph?",
          options: ["Straight line", "U-shape/parabola", "Circle", "S-shape"],
          answer: "U-shape/parabola",
          hint1: "x² creates a curved graph.",
          hint2: "It is symmetrical.",
          hint3: "It is a parabola."
        },
        {
          id: "3.6.2",
          type: "number",
          question: "For y = x² + 3, find y when x = 2.",
          answer: "7",
          hint1: "Substitute x = 2.",
          hint2: "y = 2² + 3.",
          hint3: "y = 4 + 3 = 7."
        }
      ],
      level2: [
        {
          id: "3.6.3",
          type: "number",
          question: "For y = 2x² − 10, find y when x = 3.",
          answer: "8",
          hint1: "Substitute x = 3.",
          hint2: "y = 2(3²) − 10.",
          hint3: "y = 18 − 10 = 8."
        },
        {
          id: "3.6.4",
          type: "number",
          question: "For y = x² − 4, how many x-intercepts does the graph have?",
          answer: "2",
          hint1: "Set y = 0.",
          hint2: "x² − 4 = 0.",
          hint3: "x = −2 and x = 2, so there are 2 x-intercepts."
        }
      ],
      level3: [
        {
          id: "3.6.5",
          type: "text",
          question: "Solve x² − 1 = 0 using the graph of y = x² − 1.",
          answer: "x = -1 and x = 1",
          acceptedAnswers: ["-1 and 1", "1 and -1", "x=-1,x=1", "x = −1 and x = 1"],
          hint1: "Solutions are where y = 0.",
          hint2: "The graph crosses the x-axis at x = −1 and x = 1.",
          hint3: "x = −1 and x = 1."
        }
      ]
    }
  },

  {
    id: "3.7",
    title: "Perpendicular Lines",
    slug: "perpendicular-lines",
    pageRef: "GCSE Higher expansion",
    difficulty: "higher",
    learn: {
      explanation:
        "Two lines are perpendicular if they meet at 90°. Their gradients multiply to −1. This means the perpendicular gradient is the negative reciprocal.",
      keyPoints: [
        "Perpendicular gradients multiply to −1",
        "The negative reciprocal of 2 is −1/2",
        "The negative reciprocal of −3/4 is 4/3",
        "Parallel lines have the same gradient",
        "Perpendicular lines do not have the same gradient unless one is vertical and one is horizontal",
        "To find a perpendicular line through a point, find the new gradient and substitute into y = mx + c"
      ],
      workedExamples: [
        {
          problem: "Find the gradient of a line perpendicular to y = 3x + 7.",
          steps: [
            { step: 1, instruction: "Read the original gradient", working: "m = 3" },
            { step: 2, instruction: "Find the negative reciprocal", working: "m = −1/3" }
          ],
          answer: "−1/3"
        },
        {
          problem: "Find the equation of the line perpendicular to y = 2x − 5 through (4, 1).",
          steps: [
            { step: 1, instruction: "Original gradient", working: "m = 2" },
            { step: 2, instruction: "Perpendicular gradient", working: "m = −1/2" },
            { step: 3, instruction: "Use y = mx + c", working: "1 = (−1/2)(4) + c" },
            { step: 4, instruction: "Solve", working: "1 = −2 + c, so c = 3" }
          ],
          answer: "y = −1/2x + 3"
        }
      ],
      examTips: [
        "Check perpendicular gradients by multiplying them",
        "If the product is −1, they are perpendicular",
        "A horizontal line is perpendicular to a vertical line",
        "Do not confuse perpendicular with parallel"
      ],
      commonMistakes: [
        "Only changing the sign but not flipping the fraction",
        "Only flipping the fraction but not changing the sign",
        "Thinking same gradient means perpendicular"
      ],
      whyItWorks:
        "A 90° rotation swaps the rise and run and reverses the direction. That creates the negative reciprocal gradient."
    },
    practice: {
      warmUp: [
        {
          id: "3.7.w1",
          type: "text",
          question: "Find the negative reciprocal of 4.",
          answer: "-1/4",
          acceptedAnswers: ["−1/4"],
          hint1: "Write 4 as 4/1.",
          hint2: "Flip and change the sign.",
          hint3: "−1/4"
        },
        {
          id: "3.7.w2",
          type: "text",
          question: "Find the negative reciprocal of −2/3.",
          answer: "3/2",
          hint1: "Flip −2/3 to −3/2.",
          hint2: "Change the sign.",
          hint3: "3/2"
        }
      ],
      level1: [
        {
          id: "3.7.1",
          type: "text",
          question: "Find the gradient of a line perpendicular to y = 5x + 1.",
          answer: "-1/5",
          acceptedAnswers: ["−1/5"],
          hint1: "The original gradient is 5.",
          hint2: "Find the negative reciprocal.",
          hint3: "−1/5"
        },
        {
          id: "3.7.2",
          type: "choice",
          question: "Which line is perpendicular to y = −3x + 2?",
          options: ["y = −3x + 4", "y = 3x − 1", "y = (1/3)x + 5", "y = −(1/3)x"],
          answer: "y = (1/3)x + 5",
          hint1: "The original gradient is −3.",
          hint2: "The perpendicular gradient is 1/3.",
          hint3: "Choose the line with gradient 1/3."
        }
      ],
      level2: [
        {
          id: "3.7.3",
          type: "text",
          question: "Find the equation of the line perpendicular to y = 4x − 1 passing through (8, 3).",
          answer: "y = -1/4x + 5",
          acceptedAnswers: ["y=-1/4x+5", "y = −1/4x + 5", "y = -(1/4)x + 5"],
          hint1: "The perpendicular gradient is −1/4.",
          hint2: "Use 3 = (−1/4)(8) + c.",
          hint3: "c = 5, so y = −1/4x + 5."
        }
      ],
      level3: [
        {
          id: "3.7.4",
          type: "choice",
          question: "Lines L₁ has gradient 2/3 and L₂ has gradient −3/2. What is their relationship?",
          options: ["Parallel", "Perpendicular", "Neither"],
          answer: "Perpendicular",
          hint1: "Multiply the gradients.",
          hint2: "2/3 × −3/2 = −1.",
          hint3: "They are perpendicular."
        }
      ]
    },
    aiChallenge: {
      prompt:
        "Two students disagree. Student A says 'Lines y = 2x + 3 and y = 2x − 5 are perpendicular because they look it on the graph'. Student B says 'They are parallel'. Who is right and why? Find the equation of a line that is perpendicular to y = 2x + 3 and passes through (0, 7).",
      successCriteria: [
        "Identifies that y = 2x + 3 and y = 2x − 5 have the same gradient",
        "States that Student B is correct",
        "Explains that same gradient means parallel",
        "Uses the perpendicular-gradient rule",
        "Finds perpendicular gradient −1/2",
        "Gives equation y = −1/2x + 7"
      ]
    }
  },

  {
    id: "3.8",
    title: "Transforming Graphs",
    slug: "transforming-graphs",
    pageRef: "GCSE Higher expansion",
    difficulty: "higher",
    learn: {
      explanation:
        "Graph transformations move, stretch or reflect a graph. At GCSE, the most common transformations involve shifting graphs up/down, left/right and reflecting them in the axes.",
      keyPoints: [
        "y = f(x) + a shifts the graph up by a",
        "y = f(x) − a shifts the graph down by a",
        "y = f(x + a) shifts the graph left by a",
        "y = f(x − a) shifts the graph right by a",
        "y = −f(x) reflects the graph in the x-axis",
        "y = f(−x) reflects the graph in the y-axis",
        "Inside the bracket affects x-values and often feels opposite"
      ],
      workedExamples: [
        {
          problem: "The graph y = x² has vertex (0, 0). Where is the vertex of y = x² + 4?",
          steps: [
            { step: 1, instruction: "Identify the transformation", working: "+4 is outside the function." },
            { step: 2, instruction: "Apply the transformation", working: "Shift up 4." }
          ],
          answer: "(0, 4)"
        },
        {
          problem: "y = f(x) has a point at (3, 2). Where is this point on y = f(x − 1)?",
          steps: [
            { step: 1, instruction: "Identify the inside transformation", working: "x − 1 means shift right 1." },
            { step: 2, instruction: "Apply to the coordinate", working: "(3, 2) becomes (4, 2)." }
          ],
          answer: "(4, 2)"
        },
        {
          problem: "Describe the transformation from y = x² to y = −(x − 2)² + 5.",
          steps: [
            { step: 1, instruction: "Look inside the bracket", working: "x − 2 means shift right 2." },
            { step: 2, instruction: "Look outside the function", working: "The negative sign reflects in the x-axis." },
            { step: 3, instruction: "Look at the constant", working: "+5 shifts up 5." }
          ],
          answer: "Right 2, reflected in the x-axis, then up 5."
        }
      ],
      examTips: [
        "Transform key points such as intercepts, vertices and turning points",
        "Inside transformations affect x-values",
        "Outside transformations affect y-values",
        "For y = f(x + a), the graph moves left, not right"
      ],
      commonMistakes: [
        "Thinking f(x + 3) moves right 3",
        "Reflecting in the wrong axis",
        "Moving only one point instead of the whole graph"
      ],
      whyItWorks:
        "Changing the output changes y-values directly. Changing the input changes which x-value produces the same output, so horizontal transformations work in the opposite direction."
    },
    practice: {
      warmUp: [
        {
          id: "3.8.w1",
          type: "choice",
          question: "y = f(x) + 5 means:",
          options: ["Shift up 5", "Shift down 5", "Shift left 5", "Shift right 5"],
          answer: "Shift up 5",
          hint1: "The +5 is outside the function.",
          hint2: "It adds to every y-value.",
          hint3: "Shift up 5."
        },
        {
          id: "3.8.w2",
          type: "choice",
          question: "y = f(x + 3) means:",
          options: ["Shift left 3", "Shift right 3", "Shift up 3", "Shift down 3"],
          answer: "Shift left 3",
          hint1: "The +3 is inside the function.",
          hint2: "Inside transformations move horizontally in the opposite direction.",
          hint3: "Shift left 3."
        }
      ],
      level1: [
        {
          id: "3.8.1",
          type: "text",
          question: "The graph y = x² has minimum at (0, 0). Where is the minimum of y = x² − 3?",
          answer: "(0, -3)",
          acceptedAnswers: ["(0,-3)", "0,-3", "(0,−3)"],
          hint1: "The −3 is outside the function.",
          hint2: "Shift down 3.",
          hint3: "(0, −3)"
        },
        {
          id: "3.8.2",
          type: "text",
          question: "Where is the minimum of y = (x + 4)²?",
          answer: "(-4, 0)",
          acceptedAnswers: ["(-4,0)", "−4,0", "(−4, 0)"],
          hint1: "x + 4 is inside the bracket.",
          hint2: "Shift left 4.",
          hint3: "(−4, 0)"
        }
      ],
      level2: [
        {
          id: "3.8.3",
          type: "text",
          question: "y = f(x) passes through (5, −2). Where is this point on y = f(x) − 4?",
          answer: "(5, -6)",
          acceptedAnswers: ["(5,-6)", "5,-6", "(5,−6)"],
          hint1: "This is a vertical shift.",
          hint2: "Subtract 4 from the y-coordinate.",
          hint3: "(5, −6)"
        },
        {
          id: "3.8.4",
          type: "text",
          question: "A point (3, 4) is reflected by the transformation y = −f(x). What is the new point?",
          answer: "(3, -4)",
          acceptedAnswers: ["(3,-4)", "3,-4", "(3,−4)"],
          hint1: "y = −f(x) reflects in the x-axis.",
          hint2: "The x-coordinate stays the same; the y-coordinate changes sign.",
          hint3: "(3, −4)"
        }
      ],
      level3: [
        {
          id: "3.8.5",
          type: "text",
          question: "The vertex of y = x² is (0, 0). Find the vertex of y = (x − 2)² + 1.",
          answer: "(2, 1)",
          acceptedAnswers: ["(2,1)", "2,1"],
          hint1: "x − 2 means right 2.",
          hint2: "+1 means up 1.",
          hint3: "(2, 1)"
        }
      ]
    },
    aiChallenge: {
      prompt:
        "The graph of y = x² is transformed to y = −(x + 3)² + 2. Describe the transformations and state the new vertex.",
      successCriteria: [
        "Identifies horizontal shift left 3",
        "Identifies reflection in the x-axis",
        "Identifies vertical shift up 2",
        "States vertex is (−3, 2)",
        "Explains that the negative sign outside reflects vertically"
      ]
    }
  },

  {
    id: "3.9",
    title: "Cubic Graphs",
    slug: "cubic-graphs",
    pageRef: "GCSE Higher expansion",
    difficulty: "higher",
    learn: {
      explanation:
        "A cubic graph has an x³ term. The basic graph y = x³ is an S-shaped curve passing through the origin. More complicated cubics can cross or touch the x-axis at their roots.",
      keyPoints: [
        "A cubic has the form y = ax³ + bx² + cx + d",
        "y = x³ passes through (0, 0)",
        "A positive x³ coefficient gives an overall down-left to up-right shape",
        "A negative x³ coefficient gives an overall up-left to down-right shape",
        "Roots are where y = 0",
        "A repeated root may touch the x-axis instead of crossing it",
        "A cubic can have 1, 2 or 3 distinct real roots"
      ],
      workedExamples: [
        {
          problem: "Find y when x = 2 for y = x³ − 4x.",
          steps: [
            { step: 1, instruction: "Substitute x = 2", working: "y = 2³ − 4(2)" },
            { step: 2, instruction: "Calculate", working: "y = 8 − 8 = 0" }
          ],
          answer: "0"
        },
        {
          problem: "Find the roots of y = x³ − 4x.",
          steps: [
            { step: 1, instruction: "Factorise", working: "x³ − 4x = x(x² − 4)" },
            { step: 2, instruction: "Use difference of two squares", working: "x(x − 2)(x + 2)" },
            { step: 3, instruction: "Set each factor to zero", working: "x = 0, x = 2, x = −2" }
          ],
          answer: "x = −2, 0 and 2"
        }
      ],
      examTips: [
        "Factorise where possible to find roots",
        "Check whether the graph crosses or touches the x-axis at repeated roots",
        "Use end behaviour to help sketch the graph"
      ],
      commonMistakes: [
        "Sketching a cubic as a quadratic U-shape",
        "Forgetting the root x = 0 when factorising",
        "Counting repeated roots as different x-values"
      ],
      whyItWorks:
        "Cubics are degree-3 polynomials. Their x³ term controls the overall shape and direction of the curve."
    },
    practice: {
      warmUp: [
        {
          id: "3.9.w1",
          type: "number",
          question: "Find y = x³ when x = 3.",
          answer: "27",
          hint1: "3³ means 3 × 3 × 3.",
          hint2: "3 × 3 × 3 = 27.",
          hint3: "27"
        }
      ],
      level1: [
        {
          id: "3.9.1",
          type: "number",
          question: "Find y = x³ − 1 when x = 2.",
          answer: "7",
          hint1: "2³ = 8.",
          hint2: "8 − 1 = 7.",
          hint3: "7"
        },
        {
          id: "3.9.2",
          type: "choice",
          question: "What is the general shape of y = x³?",
          options: ["U-shape", "S-curve through the origin", "Straight line"],
          answer: "S-curve through the origin",
          hint1: "A cubic is not a parabola.",
          hint2: "The basic cubic has an S-shape.",
          hint3: "S-curve through the origin."
        }
      ],
      level2: [
        {
          id: "3.9.3",
          type: "number",
          question: "How many distinct real roots does x³ − 3x = x(x² − 3) have?",
          answer: "3",
          hint1: "Set x(x² − 3) = 0.",
          hint2: "x = 0 or x² = 3.",
          hint3: "x = 0, √3 and −√3, so 3 distinct roots."
        }
      ],
      level3: [
        {
          id: "3.9.4",
          type: "number",
          question: "How many distinct roots does y = (x − 1)(x + 2)² have?",
          answer: "2",
          hint1: "x = 1 is one root.",
          hint2: "x = −2 is a repeated root.",
          hint3: "There are 2 distinct roots."
        }
      ]
    },
    aiChallenge: {
      prompt:
        "Sketch in words the graph y = x³ − 6x² + 9x. Find the roots and describe the general shape.",
      successCriteria: [
        "Factorises x³ − 6x² + 9x as x(x − 3)²",
        "Identifies roots x = 0 and x = 3",
        "States x = 3 is a repeated root",
        "Explains the graph touches the x-axis at x = 3",
        "Explains the graph crosses the x-axis at x = 0",
        "Mentions the overall shape rises to the right because the x³ coefficient is positive"
      ]
    }
  },

  {
    id: "3.10",
    title: "Reciprocal Graphs",
    slug: "reciprocal-graphs",
    pageRef: "GCSE Higher expansion",
    difficulty: "higher",
    learn: {
      explanation:
        "A reciprocal graph has the form y = k/x. It has two separate branches and asymptotes at x = 0 and y = 0. The curve gets closer and closer to the axes but never touches them.",
      keyPoints: [
        "y = k/x is undefined when x = 0",
        "The y-axis, x = 0, is a vertical asymptote",
        "The x-axis, y = 0, is a horizontal asymptote",
        "If k is positive, the branches are in quadrants 1 and 3",
        "If k is negative, the branches are in quadrants 2 and 4",
        "The larger |k| is, the further the branches sit from the origin"
      ],
      workedExamples: [
        {
          problem: "For y = 6/x, find y when x = 2 and x = −3.",
          steps: [
            { step: 1, instruction: "Substitute x = 2", working: "y = 6 ÷ 2 = 3" },
            { step: 2, instruction: "Substitute x = −3", working: "y = 6 ÷ −3 = −2" }
          ],
          answer: "(2, 3) and (−3, −2)"
        }
      ],
      examTips: [
        "Never plot a point at x = 0 for y = k/x",
        "Draw the branches approaching the axes but not touching them",
        "Check which quadrants the branches should appear in"
      ],
      commonMistakes: [
        "Drawing the graph through the origin",
        "Forgetting that division by zero is undefined",
        "Putting positive k branches in the wrong quadrants"
      ],
      whyItWorks:
        "As x gets very large, k/x gets close to 0. As x gets close to 0, k/x becomes very large in size. This creates asymptotes."
    },
    practice: {
      warmUp: [
        {
          id: "3.10.w1",
          type: "number",
          question: "Find y = 12/x when x = 4.",
          answer: "3",
          hint1: "12 ÷ 4.",
          hint2: "12 ÷ 4 = 3.",
          hint3: "3"
        }
      ],
      level1: [
        {
          id: "3.10.1",
          type: "number",
          question: "Find y = 10/x when x = −2.",
          answer: "-5",
          hint1: "10 ÷ −2.",
          hint2: "10 ÷ −2 = −5.",
          hint3: "−5"
        },
        {
          id: "3.10.2",
          type: "choice",
          question: "What are the asymptotes of y = 5/x?",
          options: ["x-axis only", "y-axis only", "Both x-axis and y-axis"],
          answer: "Both x-axis and y-axis",
          hint1: "A reciprocal graph has two asymptotes.",
          hint2: "They are x = 0 and y = 0.",
          hint3: "Both axes."
        }
      ],
      level2: [
        {
          id: "3.10.3",
          type: "choice",
          question: "Which quadrants does y = −6/x have branches in?",
          options: ["Quadrants 1 and 3", "Quadrants 2 and 4"],
          answer: "Quadrants 2 and 4",
          hint1: "k is negative.",
          hint2: "Negative reciprocal graphs have opposite-sign coordinates.",
          hint3: "Quadrants 2 and 4."
        }
      ],
      level3: [
        {
          id: "3.10.4",
          type: "number",
          question: "If y = 8/x and x = 0.5, find y.",
          answer: "16",
          hint1: "0.5 is the same as 1/2.",
          hint2: "8 ÷ 0.5 = 16.",
          hint3: "16"
        }
      ]
    },
    aiChallenge: {
      prompt:
        "A pupil claims 'y = 4/x and y = 4 − x are the same type of graph because both contain 4 and x'. Explain why they are completely different.",
      successCriteria: [
        "Identifies y = 4/x as a reciprocal graph",
        "Describes y = 4/x as having two branches and asymptotes",
        "Identifies y = 4 − x as a straight line",
        "States y = 4 − x has gradient −1 and y-intercept 4",
        "Explains that y = 4/x is undefined at x = 0",
        "Explains that y = 4 − x is defined for all real x"
      ]
    }
  },

  {
    id: "3.11",
    title: "Exponential Graphs",
    slug: "exponential-graphs",
    pageRef: "GCSE Higher expansion",
    difficulty: "higher",
    learn: {
      explanation:
        "An exponential graph has the form y = aˣ or y = k × aˣ. It models repeated multiplication. If a > 1, the graph shows growth. If 0 < a < 1, the graph shows decay.",
      keyPoints: [
        "y = aˣ passes through (0, 1) because a⁰ = 1",
        "If a > 1, the graph shows exponential growth",
        "If 0 < a < 1, the graph shows exponential decay",
        "The x-axis, y = 0, is a horizontal asymptote",
        "The graph approaches the x-axis but does not normally touch it",
        "Exponential graphs are used for compound interest, population growth and radioactive decay"
      ],
      workedExamples: [
        {
          problem: "For y = 2ˣ, find y when x = 3 and x = 0.",
          steps: [
            { step: 1, instruction: "Substitute x = 3", working: "2³ = 8" },
            { step: 2, instruction: "Substitute x = 0", working: "2⁰ = 1" }
          ],
          answer: "8 and 1"
        },
        {
          problem: "A population starts at 100 and doubles every hour. Write a formula for the population after t hours.",
          steps: [
            { step: 1, instruction: "Starting value", working: "100" },
            { step: 2, instruction: "Doubling means multiply by 2 each hour", working: "2ᵗ" },
            { step: 3, instruction: "Combine", working: "P = 100 × 2ᵗ" }
          ],
          answer: "P = 100 × 2ᵗ"
        }
      ],
      examTips: [
        "Remember a⁰ = 1",
        "Exponential growth is not the same as linear growth",
        "For decay, the base is between 0 and 1",
        "The horizontal asymptote is usually y = 0 unless the graph has been translated"
      ],
      commonMistakes: [
        "Saying the y-axis is the asymptote for y = aˣ",
        "Thinking 2⁻² = −4 instead of 1/4",
        "Using addition instead of multiplication for exponential growth"
      ],
      whyItWorks:
        "Exponential growth and decay use repeated multiplication by a constant factor, not repeated addition."
    },
    practice: {
      warmUp: [
        {
          id: "3.11.w1",
          type: "number",
          question: "Find y = 3ˣ when x = 2.",
          answer: "9",
          hint1: "3² means 3 × 3.",
          hint2: "3 × 3 = 9.",
          hint3: "9"
        }
      ],
      level1: [
        {
          id: "3.11.1",
          type: "number",
          question: "Find y = 2ˣ when x = 5.",
          answer: "32",
          hint1: "2⁵ means 2 × 2 × 2 × 2 × 2.",
          hint2: "2⁵ = 32.",
          hint3: "32"
        },
        {
          id: "3.11.2",
          type: "number",
          question: "Find y = 5ˣ when x = 0.",
          answer: "1",
          hint1: "Any non-zero number to the power 0 is 1.",
          hint2: "5⁰ = 1.",
          hint3: "1"
        }
      ],
      level2: [
        {
          id: "3.11.3",
          type: "choice",
          question: "What does y = 0.5ˣ represent?",
          options: ["Growth", "Decay"],
          answer: "Decay",
          hint1: "The base is less than 1.",
          hint2: "Values get smaller as x increases.",
          hint3: "Decay."
        },
        {
          id: "3.11.4",
          type: "number",
          question: "Find y = 2ˣ when x = −2.",
          answer: "0.25",
          acceptedAnswers: ["1/4", "0.25"],
          hint1: "2⁻² = 1/2².",
          hint2: "1/4 = 0.25.",
          hint3: "0.25"
        }
      ],
      level3: [
        {
          id: "3.11.5",
          type: "number",
          question: "Bacteria double every hour. There are 100 bacteria at the start. How many are there after 4 hours?",
          answer: "1600",
          hint1: "Use 100 × 2⁴.",
          hint2: "2⁴ = 16.",
          hint3: "100 × 16 = 1600."
        }
      ]
    },
    aiChallenge: {
      prompt:
        "A virus spreads so that the number of infected people doubles every 3 days. If 50 people are infected on day 0, write a formula for the number infected after n days and find the first day when more than 10,000 are infected.",
      successCriteria: [
        "Uses formula N = 50 × 2^(n/3)",
        "Sets up inequality 50 × 2^(n/3) > 10000",
        "Simplifies to 2^(n/3) > 200",
        "Estimates n/3 ≈ 7.64",
        "Finds n ≈ 22.9",
        "States the first full day is day 23"
      ]
    }
  },

  {
    id: "3.12",
    title: "Trigonometric Graphs",
    slug: "trigonometric-graphs",
    pageRef: "GCSE Higher expansion",
    difficulty: "higher",
    learn: {
      explanation:
        "Trigonometric graphs repeat in cycles. y = sin x and y = cos x are wave graphs with values between −1 and 1. y = tan x repeats every 180° and has vertical asymptotes.",
      keyPoints: [
        "sin x starts at 0 when x = 0°",
        "cos x starts at 1 when x = 0°",
        "sin and cos have period 360°",
        "sin and cos have amplitude 1",
        "tan x has period 180°",
        "tan x has vertical asymptotes at 90°, 270° and so on",
        "The range of sin x and cos x is −1 ≤ y ≤ 1"
      ],
      workedExamples: [
        {
          problem: "Find sin 30° and cos 60°.",
          steps: [
            { step: 1, instruction: "Recall standard values", working: "sin 30° = 1/2 and cos 60° = 1/2" }
          ],
          answer: "Both are 0.5."
        },
        {
          problem: "How many solutions does sin x = 0.5 have between 0° and 360°?",
          steps: [
            { step: 1, instruction: "Recall the first solution", working: "sin 30° = 0.5" },
            { step: 2, instruction: "Use symmetry", working: "sin is also positive in quadrant 2, so x = 150°" }
          ],
          answer: "2 solutions: 30° and 150°"
        }
      ],
      examTips: [
        "Use the CAST diagram or trig graph symmetry for solutions",
        "Check whether the interval includes endpoints",
        "Remember tan repeats every 180°, not 360°",
        "Do not give sine or cosine values outside −1 to 1"
      ],
      commonMistakes: [
        "Confusing the sine and cosine starting points",
        "Using 360° as the period of tan x",
        "Missing the second solution for sin or cos equations"
      ],
      whyItWorks:
        "Trig graphs come from coordinates on the unit circle. Repetition happens because angles repeat after full rotations."
    },
    practice: {
      warmUp: [
        {
          id: "3.12.w1",
          type: "number",
          question: "Find sin 0°.",
          answer: "0",
          hint1: "Sine starts at 0.",
          hint2: "sin 0° = 0.",
          hint3: "0"
        }
      ],
      level1: [
        {
          id: "3.12.1",
          type: "number",
          question: "Find cos 0°.",
          answer: "1",
          hint1: "Cosine starts at 1.",
          hint2: "cos 0° = 1.",
          hint3: "1"
        },
        {
          id: "3.12.2",
          type: "number",
          question: "What is the period of y = sin x in degrees?",
          answer: "360",
          hint1: "Sine repeats after one full turn.",
          hint2: "One full turn is 360°.",
          hint3: "360"
        }
      ],
      level2: [
        {
          id: "3.12.3",
          type: "number",
          question: "What is the period of y = tan x in degrees?",
          answer: "180",
          hint1: "Tan repeats more often than sine and cosine.",
          hint2: "tan x repeats every 180°.",
          hint3: "180"
        },
        {
          id: "3.12.4",
          type: "choice",
          question: "What is the range of y = cos x?",
          options: ["−1 ≤ y ≤ 1", "0 ≤ y ≤ 1", "All real numbers"],
          answer: "−1 ≤ y ≤ 1",
          hint1: "Cosine is bounded.",
          hint2: "It goes from −1 to 1.",
          hint3: "−1 ≤ y ≤ 1."
        }
      ],
      level3: [
        {
          id: "3.12.5",
          type: "number",
          question: "How many solutions does sin x = 0.5 have for 0° ≤ x ≤ 360°?",
          answer: "2",
          hint1: "sin x = 0.5 at 30°.",
          hint2: "There is another solution at 150°.",
          hint3: "2 solutions."
        }
      ]
    },
    aiChallenge: {
      prompt:
        "A Ferris wheel of radius 10 m has its centre 12 m above the ground and rotates once every 60 seconds. Write a formula for the height of a passenger above the ground at time t seconds, assuming they start at the bottom. State the maximum and minimum heights.",
      successCriteria: [
        "Recognises that the centre height is 12 m",
        "Recognises that the radius is 10 m",
        "Uses a cosine model because the passenger starts at the bottom",
        "Sets angle as 6t degrees or 2πt/60 radians",
        "Gives h(t) = 12 − 10cos(6t°), or equivalent",
        "States maximum height is 22 m",
        "States minimum height is 2 m"
      ]
    }
  },

  {
    id: "3.13",
    title: "Equation of a Circle",
    slug: "circle-equation",
    pageRef: "GCSE Higher expansion",
    difficulty: "higher",
    learn: {
      explanation:
        "A circle centred at the origin with radius r has equation x² + y² = r². This comes directly from Pythagoras because every point on the circle is the same distance from the centre.",
      keyPoints: [
        "x² + y² = r² is a circle centred at the origin",
        "The radius is r, not r²",
        "x² + y² = 25 has radius 5",
        "To check whether a point lies on the circle, substitute x and y",
        "Where the circle meets the x-axis, y = 0",
        "Where the circle meets the y-axis, x = 0",
        "A circle centred at (a, b) has equation (x − a)² + (y − b)² = r², but GCSE usually focuses on centre origin"
      ],
      workedExamples: [
        {
          problem: "Find the equation of a circle centred at the origin with radius 4.",
          steps: [
            { step: 1, instruction: "Use x² + y² = r²", working: "r = 4" },
            { step: 2, instruction: "Square the radius", working: "4² = 16" }
          ],
          answer: "x² + y² = 16"
        },
        {
          problem: "Does (3, 4) lie on x² + y² = 25?",
          steps: [
            { step: 1, instruction: "Substitute x = 3 and y = 4", working: "3² + 4² = 9 + 16 = 25" },
            { step: 2, instruction: "Compare with the equation", working: "25 = 25, so the point lies on the circle." }
          ],
          answer: "Yes"
        }
      ],
      examTips: [
        "Take the square root of the number on the right to find the radius",
        "Use substitution carefully to test points",
        "When finding axis intercepts, set one coordinate equal to 0"
      ],
      commonMistakes: [
        "Saying x² + y² = 25 has radius 25",
        "Forgetting both positive and negative intercepts",
        "Substituting only one coordinate when checking a point"
      ],
      whyItWorks:
        "For any point (x, y) on a circle centred at the origin, the horizontal and vertical distances form a right-angled triangle with hypotenuse r. Therefore x² + y² = r²."
    },
    practice: {
      warmUp: [
        {
          id: "3.13.w1",
          type: "number",
          question: "Find the radius of the circle x² + y² = 49.",
          answer: "7",
          hint1: "The number on the right is r².",
          hint2: "r = √49.",
          hint3: "7"
        }
      ],
      level1: [
        {
          id: "3.13.1",
          type: "number",
          question: "Find the radius of x² + y² = 100.",
          answer: "10",
          hint1: "r² = 100.",
          hint2: "r = √100.",
          hint3: "10"
        },
        {
          id: "3.13.2",
          type: "choice",
          question: "Does (5, 0) lie on x² + y² = 25?",
          options: ["Yes", "No"],
          answer: "Yes",
          hint1: "Substitute x = 5 and y = 0.",
          hint2: "5² + 0² = 25.",
          hint3: "Yes."
        }
      ],
      level2: [
        {
          id: "3.13.3",
          type: "choice",
          question: "Does (3, 5) lie on x² + y² = 30?",
          options: ["Yes", "No"],
          answer: "No",
          hint1: "Substitute x = 3 and y = 5.",
          hint2: "3² + 5² = 9 + 25 = 34.",
          hint3: "34 ≠ 30, so no."
        },
        {
          id: "3.13.4",
          type: "text",
          question: "Find the equation of a circle with radius 8 centred at the origin.",
          answer: "x^2 + y^2 = 64",
          acceptedAnswers: ["x² + y² = 64", "x^2+y^2=64", "x²+y²=64"],
          hint1: "Use x² + y² = r².",
          hint2: "8² = 64.",
          hint3: "x² + y² = 64."
        }
      ],
      level3: [
        {
          id: "3.13.5",
          type: "number",
          question: "The circle x² + y² = 169 cuts the positive y-axis at what y-value?",
          answer: "13",
          hint1: "On the y-axis, x = 0.",
          hint2: "y² = 169.",
          hint3: "y = 13 for the positive y-axis."
        }
      ]
    },
    aiChallenge: {
      prompt:
        "A circle of radius 5 is centred at the origin. Find the coordinates of all four points where this circle meets the axes and explain how you found them.",
      successCriteria: [
        "Writes the equation x² + y² = 25",
        "Sets y = 0 for x-axis intercepts",
        "Finds x = ±5",
        "Gives (5, 0) and (−5, 0)",
        "Sets x = 0 for y-axis intercepts",
        "Finds y = ±5",
        "Gives (0, 5) and (0, −5)"
      ]
    }
  },

  {
    id: "3.14",
    title: "Tangent to a Circle",
    slug: "tangent-to-circle",
    pageRef: "GCSE Higher expansion",
    difficulty: "higher",
    learn: {
      explanation:
        "A tangent touches a circle at exactly one point. At the point of contact, the tangent is perpendicular to the radius. This allows you to calculate the tangent gradient and equation.",
      keyPoints: [
        "A tangent touches a circle at one point",
        "The radius and tangent meet at 90°",
        "Find the radius gradient from the centre to the point of contact",
        "The tangent gradient is the negative reciprocal of the radius gradient",
        "Use y − y₁ = m(x − x₁) or y = mx + c to find the tangent equation",
        "For x² + y² = r² at (x₁, y₁), the tangent can also be written as x₁x + y₁y = r²"
      ],
      workedExamples: [
        {
          problem: "Find the equation of the tangent to x² + y² = 25 at (3, 4).",
          steps: [
            { step: 1, instruction: "Check the point is on the circle", working: "3² + 4² = 9 + 16 = 25" },
            { step: 2, instruction: "Find radius gradient", working: "Gradient from (0,0) to (3,4) = 4/3" },
            { step: 3, instruction: "Find tangent gradient", working: "Negative reciprocal of 4/3 is −3/4" },
            { step: 4, instruction: "Use point-slope form", working: "y − 4 = −3/4(x − 3)" },
            { step: 5, instruction: "Rearrange", working: "4y − 16 = −3x + 9, so 3x + 4y = 25" }
          ],
          answer: "3x + 4y = 25"
        }
      ],
      examTips: [
        "Always check the point lies on the circle if asked",
        "Use negative reciprocal for the tangent gradient",
        "A radius to (r, 0) is horizontal, so the tangent is vertical",
        "A vertical tangent has equation x = constant"
      ],
      commonMistakes: [
        "Using the radius gradient as the tangent gradient",
        "Forgetting to change sign when finding the negative reciprocal",
        "Trying to write a vertical tangent in the form y = mx + c"
      ],
      whyItWorks:
        "The radius is perpendicular to the tangent at the point of contact. So once you know the radius gradient, the perpendicular-gradient rule gives the tangent gradient."
    },
    practice: {
      warmUp: [
        {
          id: "3.14.w1",
          type: "text",
          question: "Find the gradient perpendicular to 4/3.",
          answer: "-3/4",
          acceptedAnswers: ["−3/4"],
          hint1: "Use the negative reciprocal.",
          hint2: "Flip 4/3 and change the sign.",
          hint3: "−3/4"
        }
      ],
      level1: [
        {
          id: "3.14.1",
          type: "number",
          question: "The radius from the origin to (4, 0) has what gradient?",
          answer: "0",
          hint1: "The radius is horizontal.",
          hint2: "Horizontal lines have gradient 0.",
          hint3: "0"
        },
        {
          id: "3.14.2",
          type: "number",
          question: "The tangent at (5, 0) on x² + y² = 25 has equation x = ?",
          answer: "5",
          hint1: "The radius is horizontal.",
          hint2: "The tangent is vertical.",
          hint3: "x = 5."
        }
      ],
      level2: [
        {
          id: "3.14.3",
          type: "text",
          question: "Find the tangent gradient at (3, 4) on x² + y² = 25.",
          answer: "-3/4",
          acceptedAnswers: ["−3/4"],
          hint1: "The radius gradient is 4/3.",
          hint2: "The tangent gradient is the negative reciprocal.",
          hint3: "−3/4"
        }
      ],
      level3: [
        {
          id: "3.14.4",
          type: "number",
          question: "Find the y-intercept of the tangent 3x + 4y = 25.",
          answer: "6.25",
          acceptedAnswers: ["25/4", "6.25"],
          hint1: "Set x = 0.",
          hint2: "4y = 25.",
          hint3: "y = 25/4 = 6.25."
        }
      ]
    },
    aiChallenge: {
      prompt:
        "Find the equation of the tangent to the circle x² + y² = 100 at the point (−6, 8). Show every step.",
      successCriteria: [
        "Verifies that (−6, 8) lies on the circle: 36 + 64 = 100",
        "Calculates radius gradient as 8/−6 = −4/3",
        "Finds tangent gradient as 3/4",
        "Uses y − 8 = 3/4(x + 6)",
        "Rearranges correctly",
        "Gives final equation y = 3/4x + 12.5 or 3x − 4y + 50 = 0"
      ]
    }
  }
];

export default graphsTopics;